/* External */
import {
  memo, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';

/* Ott-common */
import NpawMonitoring from '@dtvgo/npaw-monitoring';
import { getMSOProviderFormatted, USER_TYPES } from '@dtvgo/utils';

/* Utils */
import { BUSINESS_CAMPAIGN } from '~/utils/constants';
import { useUserAccount } from '../../providers/UserProvider';
import { useDeviceInfo } from '../../providers/DeviceInfoProvider';
import platform from '../../utils/platform';
import { APP_VERSION, isLogsNpawActive, NPAW_P2P_ACTIVE } from '../../utils/env';
import { Content } from '../../storage/models';

const NPAW_ACCOUNT_CODE = import.meta.env.VITE_NPAW_ACCOUNT_CODE;
const APP_NAME = `${import.meta.env.VITE_NPAW_APP_NAME} ${platform.npaw.name}`;
function NpawMonitoringContainer({
  isLive = false,
  content = undefined,
  channelName = '',
  channelId = '',
  savedProgress = 0,
  playSource = undefined,
  error = undefined,
  provider = '',
}) {
  const {
    uid: userId,
    accountType: userType,
    msoProvider,
    businessUnit,
    subscriberId,
  } = useUserAccount() || {};
  const { version, sdkVersion, modelName } = useDeviceInfo() || {};
  const userProfile = JSON.parse(localStorage.getItem('profile')) || {};
  const profileId = Object.values(userProfile).length === 0 ? userId : userProfile?.id;
  const msoFormatted = getMSOProviderFormatted({ msoProvider, businessUnit });
  const userTypeNpaw = BUSINESS_CAMPAIGN.has(msoProvider) ? USER_TYPES.FREE : userType;
  const [binaryAppVersion, setBinaryAppVersion] = useState('');

  useEffect(() => {
    const awaitAppVersion = async () => {
      try {
        const versionSend = await platform.getAppVersion();
        setBinaryAppVersion(versionSend);
      } catch (error_) {
        console.error('[error app version]:', error_);
      }
    };

    awaitAppVersion();
  }, []);

  const playerInfo = {
    PLAYER_NAME: APP_NAME,
    APP_VERSION: `${APP_NAME} - ${APP_VERSION}`,
  };

  const deviceMetadata = useMemo(() => ({
    model: modelName,
    osVersion: sdkVersion,
    firmwareVersion: binaryAppVersion || version,
  }), [modelName, sdkVersion, version, binaryAppVersion]);

  return (
    <NpawMonitoring
      isLive={isLive}
      accountCode={NPAW_ACCOUNT_CODE}
      userId={userId}
      profileId={profileId}
      userType={userTypeNpaw}
      content={content}
      playerInfo={playerInfo}
      playSource={playSource}
      channelName={channelName}
      channelId={channelId}
      vodPlayStartTime={savedProgress}
      error={error}
      deviceMetadata={deviceMetadata}
      msoProvider={msoFormatted}
      providerId={provider}
      subscriberId={subscriberId}
      activeLogsNpaw={isLogsNpawActive}
      p2pEnabled={NPAW_P2P_ACTIVE}
      platform={platform.platformName}
    />
  );
}

NpawMonitoringContainer.propTypes = {
  isLive: PropTypes.bool,
  content: PropTypes.instanceOf(Content),
  channelName: PropTypes.string,
  channelId: PropTypes.string,
  savedProgress: PropTypes.number,
  playSource: PropTypes.string,
  error: PropTypes.shape({}),
  provider: PropTypes.string,
};

export default memo(NpawMonitoringContainer);
